import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { CalendarPlus, Video } from "lucide-react";
import { rpc, useAction, useOrg } from "@vixio/data";
import {
  Button, ConfirmDialog, Dialog, EmptyState, PageHeader, Panel, Select, Skeleton, StatusFromMap, TextArea, formatDate, formatTime,
  type StatusMap,
} from "@vixio/ui";
import { LOCATION, roomOpen, shortDay, tzLabel, useMyMeetings, type MyMeeting } from "./api";

const MY_STATUS: StatusMap = {
  scheduled: { label: "marcada", tone: "info" },
  done: { label: "realizada", tone: "ok" },
  canceled: { label: "cancelada", tone: "neutral" },
};

function useNow(ms = 30_000): number {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const t = window.setInterval(() => setNow(Date.now()), ms);
    return () => window.clearInterval(t);
  }, [ms]);
  return now;
}

function when(m: MyMeeting): string {
  return `${formatDate(m.starts_at)}, ${formatTime(m.starts_at)} às ${formatTime(m.ends_at)}`;
}

function JoinButton({ m, now, small }: { m: MyMeeting; now: number; small?: boolean }) {
  const navigate = useNavigate();
  if (!m.room_id || m.status === "canceled") return null;
  if (roomOpen(m, now)) {
    return (
      <Button variant="primary" size={small ? "sm" : "md"} icon={<Video />} onClick={() => navigate(`/portal/reunioes/sala/${m.room_id}`)}>
        Entrar na sala
      </Button>
    );
  }
  if (Date.parse(m.opens_at) > now) {
    return <span className="text-2" style={{ fontSize: "var(--fs-12)" }}>A sala abre às {formatTime(m.opens_at)} de {formatDate(m.opens_at)}</span>;
  }
  return null;
}

/** Início do portal: próximas reuniões com o botão de entrar quando a sala abre. */
export function ClientMeetingsHome() {
  const org = useOrg();
  const q = useMyMeetings(org.id);
  const now = useNow();
  const next = (q.data ?? []).filter((m) => m.status === "scheduled" && Date.parse(m.closes_at) >= now).slice(0, 3);
  if (q.isLoading) return <Skeleton lines={2} />;
  return (
    <section className="vx-block" aria-labelledby="portal-reunioes" style={{ marginTop: 0 }}>
      <div className="vx-block__head">
        <h2 className="vx-block__title" id="portal-reunioes">Próximas reuniões</h2>
        <Link to="/portal/reunioes">Agendar ou ver todas</Link>
      </div>
      {next.length === 0 ? (
        <div className="vx-next__done">
          <CalendarPlus aria-hidden="true" style={{ width: 20, height: 20, color: "var(--accent)", flexShrink: 0 }} />
          <span>
            Nenhuma reunião marcada. <Link to="/portal/reunioes">Agendar uma conversa</Link>
          </span>
        </div>
      ) : (
        <ul className="vx-next">
          {next.map((m) => (
            <li key={`${m.kind}-${m.id}`} className={`vx-next__item${roomOpen(m, now) ? " vx-next__item--ok" : ""}`}>
              <span className="vx-next__icon" aria-hidden="true">
                <Video />
              </span>
              <div style={{ minWidth: 0 }}>
                <p className="vx-next__title">{m.title}</p>
                <p className="vx-next__desc">
                  {when(m)}
                  {m.room_id ? " · online, pela plataforma" : m.location ? ` · ${LOCATION[m.location] ?? m.location}` : ""}
                </p>
              </div>
              <div className="vx-next__action">
                <JoinButton m={m} now={now} small />
              </div>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}

/** Reuniões do cliente: próximas, agendar pela agenda da equipe, entrar na sala, cancelar e histórico. */
export function ClientMeetingsPage() {
  const org = useOrg();
  const q = useMyMeetings(org.id, true);
  const types = useQuery({ queryKey: ["agenda", "client_types", org.id], queryFn: () => rpc("agenda", "client_types", { p_org: org.id }) });
  const now = useNow();
  const [booking, setBooking] = useState(false);
  const [canceling, setCanceling] = useState<MyMeeting | null>(null);
  const cancel = useAction((reason: string) => rpc("agenda", "client_cancel", { p_booking: canceling!.id, p_reason: reason || null }), {
    invalidate: [["agenda"]],
    success: "Reserva cancelada",
    onSuccess: () => setCanceling(null),
  });
  const all = q.data ?? [];
  const upcoming = all.filter((m) => m.status === "scheduled" && Date.parse(m.closes_at) >= now);
  const past = all.filter((m) => !upcoming.includes(m)).reverse();
  const canBook = (types.data ?? []).length > 0;
  return (
    <>
      <PageHeader
        title="Reuniões"
        description="As reuniões acontecem aqui mesmo, por vídeo. A sala abre 15 minutos antes do horário; é só entrar."
        actions={
          canBook ? (
            <Button variant="primary" icon={<CalendarPlus />} onClick={() => setBooking(true)}>
              Agendar conversa
            </Button>
          ) : null
        }
      />
      {q.isLoading ? (
        <Skeleton lines={4} />
      ) : (
        <>
          <section className="vx-block" aria-labelledby="reunioes-proximas">
            <div className="vx-block__head">
              <h2 className="vx-block__title" id="reunioes-proximas">Próximas</h2>
            </div>
            {upcoming.length === 0 ? (
              <Panel>
                <EmptyState
                  title="Nenhuma reunião marcada"
                  actions={canBook ? <Button icon={<CalendarPlus />} onClick={() => setBooking(true)}>Agendar conversa</Button> : null}
                >
                  {canBook ? "Escolha um horário livre na agenda da equipe." : "Quando a equipe marcar uma reunião com você, ela aparece aqui."}
                </EmptyState>
              </Panel>
            ) : (
              <div className="vx-cards">
                {upcoming.map((m) => (
                  <article key={`${m.kind}-${m.id}`} className={`vx-card${roomOpen(m, now) ? " vx-card--accent" : ""}`} aria-labelledby={`reu-${m.id}`}>
                    <div className="vx-card__head">
                      <div style={{ minWidth: 0 }}>
                        <h3 className="vx-card__title" id={`reu-${m.id}`}>
                          {m.title}
                        </h3>
                        <p className="vx-card__sub">{when(m)}</p>
                      </div>
                      <StatusFromMap map={MY_STATUS} value={m.status} />
                    </div>
                    <p className="text-2" style={{ margin: 0, fontSize: "var(--fs-13)" }}>
                      {m.room_id ? "Online, pela sala de vídeo da plataforma." : m.location ? LOCATION[m.location] ?? m.location : "Local a combinar."}
                    </p>
                    <div className="vx-card__foot">
                      <JoinButton m={m} now={now} />
                      {m.can_cancel ? (
                        <Button variant="ghost" size="sm" onClick={() => setCanceling(m)}>
                          Cancelar
                        </Button>
                      ) : null}
                    </div>
                  </article>
                ))}
              </div>
            )}
          </section>
          {past.length > 0 ? (
            <section className="vx-block" aria-labelledby="reunioes-anteriores">
              <div className="vx-block__head">
                <h2 className="vx-block__title" id="reunioes-anteriores">Anteriores</h2>
              </div>
              <Panel flush>
                <ul className="vx-phases" style={{ padding: "12px 16px" }}>
                  {past.map((m) => (
                    <li key={`${m.kind}-${m.id}`} className="vx-phase" style={{ gridTemplateColumns: "minmax(0, 1fr) auto" }}>
                      <div style={{ minWidth: 0 }}>
                        <p className="vx-phase__name">{m.title}</p>
                        <p className="vx-phase__meta">{when(m)}</p>
                      </div>
                      <StatusFromMap map={MY_STATUS} value={m.status} />
                    </li>
                  ))}
                </ul>
              </Panel>
            </section>
          ) : null}
        </>
      )}
      {booking ? <ScheduleDialog onClose={() => setBooking(false)} /> : null}
      <ConfirmDialog
        open={canceling !== null}
        onOpenChange={(o) => !o && setCanceling(null)}
        title={`Cancelar ${canceling?.title ?? ""}`}
        confirmLabel="Cancelar reserva"
        destructive
        reason={{ label: "Motivo (opcional)", min: 0, max: 1000 }}
        onConfirm={(reason) => cancel.mutateAsync(reason ?? "")}
      >
        <p>{canceling ? when(canceling) : null}. A equipe é avisada e o horário fica livre para outra pessoa.</p>
      </ConfirmDialog>
    </>
  );
}

function ScheduleDialog({ onClose }: { onClose: () => void }) {
  const org = useOrg();
  const navigate = useNavigate();
  const types = useQuery({ queryKey: ["agenda", "client_types", org.id], queryFn: () => rpc("agenda", "client_types", { p_org: org.id }) });
  const [typeKey, setTypeKey] = useState("");
  const [day, setDay] = useState("");
  const [slot, setSlot] = useState<string | null>(null);
  const [notes, setNotes] = useState("");
  const key = typeKey || types.data?.[0]?.key || "";
  const type = types.data?.find((t) => t.key === key);
  const slots = useQuery({
    queryKey: ["agenda", "client_slots", org.id, key],
    queryFn: () => rpc("agenda", "client_slots", { p_org: org.id, p_type_key: key }),
    enabled: !!key,
  });
  const byDay = useMemo(() => {
    const m = new Map<string, Array<{ starts_at: string; local_time: string }>>();
    for (const s of slots.data?.slots ?? []) {
      const [d, mo, y] = s.local_date.split("/");
      const iso = `${y}-${mo}-${d}`;
      m.set(iso, [...(m.get(iso) ?? []), s]);
    }
    return m;
  }, [slots.data]);
  const days = [...byDay.keys()];
  const currentDay = day && byDay.has(day) ? day : days[0] ?? "";
  const save = useAction(() => rpc("agenda", "client_book", { p_org: org.id, p_type_key: key, p_starts_at: slot!, p_notes: notes.trim() || null }), {
    invalidate: [["agenda"]],
    success: (r) => `Reunião marcada para ${r.local_date}, às ${r.local_time}`,
    onSuccess: () => {
      onClose();
      navigate("/portal/reunioes");
    },
  });
  return (
    <Dialog
      open
      size="lg"
      onOpenChange={(o) => !o && onClose()}
      title="Agendar conversa"
      description={type ? `${type.title} · ${type.duration_min} minutos · ${tzLabel(type.timezone)}` : undefined}
      onSubmit={() => slot && save.mutate(undefined)}
      footer={
        <>
          <Button onClick={onClose}>Cancelar</Button>
          <Button type="submit" variant="primary" loading={save.isPending} disabled={!slot}>
            Confirmar horário
          </Button>
        </>
      }
    >
      <div className="vx-stack vx-stack--lg">
        {(types.data ?? []).length > 1 ? (
          <Select
            label="Tipo de conversa"
            value={key}
            onChange={(v) => {
              setTypeKey(v);
              setSlot(null);
              setDay("");
            }}
            options={(types.data ?? []).map((t) => ({ value: t.key, label: `${t.title} (${t.duration_min} min)` }))}
          />
        ) : null}
        {type?.description ? <p className="text-2" style={{ margin: 0 }}>{type.description}</p> : null}
        {slots.isLoading ? (
          <Skeleton lines={3} />
        ) : days.length === 0 ? (
          <EmptyState title="Sem horários livres nos próximos dias">Abra um chamado e combinamos outro horário.</EmptyState>
        ) : (
          <>
            <div>
              <p className="vx-field__label" id="dias-label">Dia</p>
              <div className="vx-slots" role="group" aria-labelledby="dias-label">
                {days.map((d) => (
                  <button
                    key={d}
                    type="button"
                    className="vx-slot"
                    aria-pressed={d === currentDay}
                    onClick={() => {
                      setDay(d);
                      setSlot(null);
                    }}
                  >
                    {shortDay(d)}
                  </button>
                ))}
              </div>
            </div>
            <div>
              <p className="vx-field__label" id="horas-label">Horário</p>
              <div className="vx-slots" role="group" aria-labelledby="horas-label">
                {(byDay.get(currentDay) ?? []).map((s) => (
                  <button key={s.starts_at} type="button" className="vx-slot" aria-pressed={slot === s.starts_at} onClick={() => setSlot(s.starts_at)}>
                    {s.local_time}
                  </button>
                ))}
              </div>
            </div>
          </>
        )}
        <TextArea label="Quer adiantar o assunto? (opcional)" value={notes} onChange={setNotes} rows={3} maxLength={2000} />
      </div>
    </Dialog>
  );
}
